import { displayUserName } from "@modules/user/deletedUser";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Check, ChevronsUpDown } from "lucide-react";

import {
  PesTestDocument,
  useTransferFarmsLazyQuery,
  useTransferFieldsLazyQuery,
  useTransferOrganisationsLazyQuery,
  useTransferPesTestMutation,
} from "generated/hooks";
import { DependencyType } from "@di/DependencyType";
import { useInjection } from "@di/DependencyContext";
import { UserService } from "~/modules/user/user.service";
import { useFetchAll } from "~/hooks/useFetchAll";
import { parseServerError } from "~/util/parseError";
import { cn } from "~/lib/utils";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

type TransferStep = "user" | "organisation" | "farm" | "field";
type ChoiceMode = "current" | "change";
type FarmMode = "current" | "existing" | "new";
type FieldMode = "current" | "existing" | "new";

interface NamedOption {
  id: string;
  name: string;
}

interface UserOption extends NamedOption {
  email?: string | null;
  organisationMemberships: NamedOption[];
}

interface SummaryRow {
  key: TransferStep;
  label: string;
  current: string;
  next?: string | null;
}

interface ChoiceButtonOption<TValue extends string> {
  value: TValue;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface SelectionComboboxOption {
  value: string;
  label: string;
}

export interface TransferTestDialogTest {
  id: string;
  createdBy?: {
    id: string;
    fullName?: string | null;
    email?: string | null;
    organisationMemberships?: Array<{
      id: string;
      name: string;
    }> | null;
  } | null;
  organisation?: NamedOption | null;
  farm?: NamedOption | null;
  field?: NamedOption | null;
}

export interface TransferTestDialogProps {
  isOpen: boolean;
  test: TransferTestDialogTest;
  onCancel: () => void;
  onTransferred: () => Promise<void> | void;
}

const STEPS: Array<{ key: TransferStep; label: string }> = [
  { key: "user", label: "User" },
  { key: "organisation", label: "Organisation" },
  { key: "farm", label: "Farm" },
  { key: "field", label: "Field" },
];

const getUserLabel = (user?: UserOption | null) =>
  user?.name || user?.email || "Unknown user";

const toUserOption = (
  user?: TransferTestDialogTest["createdBy"]
): UserOption | null =>
  user?.id
    ? {
        id: user.id,
        name: displayUserName(user, "Unknown user"),
        email: user.email,
        organisationMemberships:
          user.organisationMemberships?.map((organisation) => ({
            id: organisation.id,
            name: organisation.name,
          })) ?? [],
      }
    : null;

const toSelectionOptions = (items: NamedOption[]): SelectionComboboxOption[] =>
  items
    .filter((item) => Boolean(item.id && item.name))
    .map((item) => ({ value: item.id, label: item.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

const valuesEqual = (left?: string | null, right?: string | null) =>
  (left ?? "") === (right ?? "");

const StepSummaryRow = ({
  label,
  current,
  next,
}: {
  label: string;
  current: string;
  next?: string | null;
}) => {
  const changed = next && next !== current;

  return (
    <div className="grid grid-cols-[7.5rem_1fr] gap-3 text-sm">
      <dt className="font-medium text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-foreground">
        {changed ? (
          <span className="inline-flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-muted-foreground line-through">
              {current}
            </span>
            <span aria-hidden="true">-&gt;</span>
            <span className="font-medium">{next}</span>
          </span>
        ) : (
          <span>{next ?? current}</span>
        )}
      </dd>
    </div>
  );
};

const TransferSummary = ({ rows }: { rows: SummaryRow[] }) => {
  if (rows.length === 0) {
    return null;
  }

  return (
    <dl
      data-testid="transfer-summary"
      className="space-y-2 rounded-md border bg-muted/30 p-3"
    >
      {rows.map((row) => (
        <StepSummaryRow
          key={row.key}
          label={row.label}
          current={row.current}
          next={row.next}
        />
      ))}
    </dl>
  );
};

const ChoiceButtonGroup = <TValue extends string>({
  value,
  options,
  onChange,
}: {
  value: TValue;
  options: ChoiceButtonOption<TValue>[];
  onChange: (value: TValue) => void;
}) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {options.map((option) => {
      const selected = option.value === value;

      return (
        <button
          key={option.value}
          type="button"
          disabled={option.disabled}
          className={cn(
            "flex min-h-12 w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm font-medium transition-colors",
            selected
              ? "border-primary/60 bg-primary/10 text-primary"
              : "border-border bg-background text-foreground hover:bg-accent",
            option.disabled &&
              "cursor-not-allowed border-border bg-muted/40 text-muted-foreground opacity-70 hover:bg-muted/40"
          )}
          onClick={() => {
            if (!option.disabled) {
              onChange(option.value);
            }
          }}
        >
          <span className="min-w-0">
            <span className="block truncate">{option.label}</span>
            {option.description && (
              <span className="mt-0.5 block truncate text-xs font-normal text-muted-foreground">
                {option.description}
              </span>
            )}
          </span>
          {selected && <Check className="size-4 shrink-0" aria-hidden="true" />}
        </button>
      );
    })}
  </div>
);

const TransferCombobox = ({
  placeholder,
  options,
  emptyMessage = "No results",
  onSelect,
  loading,
  disabled,
  searchPlaceholder = "Search...",
}: {
  placeholder: string;
  options: SelectionComboboxOption[];
  emptyMessage?: string;
  onSelect: (value: string) => void;
  loading?: boolean;
  disabled?: boolean;
  searchPlaceholder?: string;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!disabled) {
          setOpen(nextOpen);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "inline-flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors",
            "hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
        >
          <span className="truncate text-left font-normal text-muted-foreground">
            {placeholder}
          </span>
          <ChevronsUpDown
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {loading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            ) : (
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      onSelect(option.value);
                      setOpen(false);
                    }}
                  >
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const TransferTestDialog = ({
  isOpen,
  test,
  onCancel,
  onTransferred,
}: TransferTestDialogProps) => {
  const userService = useInjection<UserService>(DependencyType.UserService);
  const userServiceRef = useRef(userService);
  const currentUser = useMemo(
    () => toUserOption(test.createdBy),
    [test.createdBy]
  );
  const currentOrganisation = test.organisation ?? null;
  const currentFarm = test.farm ?? null;
  const currentField = test.field ?? null;

  const [stepIndex, setStepIndex] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [userMode, setUserMode] = useState<ChoiceMode>("current");
  const [organisationMode, setOrganisationMode] =
    useState<ChoiceMode>("current");
  const [farmMode, setFarmMode] = useState<FarmMode>("current");
  const [fieldMode, setFieldMode] = useState<FieldMode>("current");
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(
    currentUser
  );
  const [selectedOrganisation, setSelectedOrganisation] =
    useState<NamedOption | null>(currentOrganisation);
  const [selectedFarm, setSelectedFarm] = useState<NamedOption | null>(
    currentFarm
  );
  const [selectedField, setSelectedField] = useState<NamedOption | null>(
    currentField
  );
  const [newFarmName, setNewFarmName] = useState("");
  const [newFieldName, setNewFieldName] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const latestUserSearchRequestRef = useRef(0);

  const [transferPesTest, transferResult] = useTransferPesTestMutation();
  const [fetchOrganisations] = useTransferOrganisationsLazyQuery();
  const [fetchFarms] = useTransferFarmsLazyQuery();
  const [fetchFields] = useTransferFieldsLazyQuery();

  useEffect(() => {
    userServiceRef.current = userService;
  }, [userService]);

  const { data: organisations, loading: organisationsLoading } = useFetchAll(
    fetchOrganisations,
    (data) => data.organisations,
    { variables: { after: null } }
  );
  const selectedOrganisationId = selectedOrganisation?.id;
  const { data: farms, loading: farmsLoading } = useFetchAll(
    fetchFarms,
    (data) => data.organisationById?.farms,
    {
      skip: !selectedOrganisationId,
      variables: {
        organisationId: selectedOrganisationId ?? "",
        after: null,
      },
    }
  );
  const selectedFarmId = selectedFarm?.id;
  const { data: fields, loading: fieldsLoading } = useFetchAll(
    fetchFields,
    (data) => data.farm?.fields,
    {
      skip: !selectedFarmId || farmMode === "new",
      variables: {
        farmId: selectedFarmId ?? "",
        after: null,
      },
    }
  );
  const canUseCurrentFarm = Boolean(
    selectedOrganisation?.id &&
      currentOrganisation?.id &&
      selectedOrganisation.id === currentOrganisation.id &&
      currentFarm
  );
  const canUseCurrentField = Boolean(
    farmMode !== "new" &&
      selectedFarm?.id &&
      currentFarm?.id &&
      selectedFarm.id === currentFarm.id &&
      currentField
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setStepIndex(0);
    setConfirmationOpen(false);
    setUserMode("current");
    setOrganisationMode("current");
    setFarmMode(currentFarm ? "current" : "existing");
    setFieldMode(currentField ? "current" : "existing");
    setSelectedUser(currentUser);
    setSelectedOrganisation(currentOrganisation);
    setSelectedFarm(currentFarm);
    setSelectedField(currentField);
    setNewFarmName("");
    setNewFieldName("");
    setUserSearch("");
    setUserOptions([]);
    setLocalError(null);
  }, [currentFarm, currentField, currentOrganisation, currentUser, isOpen]);

  useEffect(() => {
    if (!isOpen || userMode !== "change") {
      latestUserSearchRequestRef.current += 1;
      return;
    }

    const requestId = latestUserSearchRequestRef.current + 1;
    latestUserSearchRequestRef.current = requestId;

    const timeout = window.setTimeout(() => {
      const searchTerm = userSearch.trim();
      if (searchTerm.length < 2) {
        if (requestId === latestUserSearchRequestRef.current) {
          setUserOptions([]);
          setUsersLoading(false);
        }
        return;
      }

      setUsersLoading(true);
      userServiceRef.current
        .getUsers({
          after: null,
          searchTerm,
          role: null,
          organisationId: null,
          sortKey: "fullName",
          sortDirection: "ASC",
        })
        .then((result) => {
          if (requestId !== latestUserSearchRequestRef.current) {
            return;
          }

          setUserOptions(
            result.users.map((user) => ({
              id: user.id,
              name:
                displayUserName(user, "Unknown user"),
              email: user.email,
              organisationMemberships:
                user.organisationMemberships?.filter(Boolean).map((org) => ({
                  id: org.id,
                  name: org.name,
                })) ?? [],
            }))
          );
        })
        .catch((error) => {
          if (requestId !== latestUserSearchRequestRef.current) {
            return;
          }

          setLocalError(parseServerError(error).message);
          setUserOptions([]);
        })
        .finally(() => {
          if (requestId === latestUserSearchRequestRef.current) {
            setUsersLoading(false);
          }
        });
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [isOpen, userMode, userSearch]);

  useEffect(() => {
    if (organisationMode === "current") {
      if (selectedOrganisation?.id !== currentOrganisation?.id) {
        setSelectedOrganisation(currentOrganisation);
      }
      return;
    }

    if (
      !selectedOrganisation ||
      selectedOrganisation.id === currentOrganisation?.id
    ) {
      return;
    }

    setSelectedFarm(null);
    setSelectedField(null);
    if (farmMode === "current") {
      setFarmMode("existing");
    }
    if (fieldMode === "current") {
      setFieldMode("existing");
    }
  }, [
    currentOrganisation,
    farmMode,
    fieldMode,
    organisationMode,
    selectedOrganisation,
  ]);

  useEffect(() => {
    if (farmMode === "current" && canUseCurrentFarm) {
      setSelectedFarm(currentFarm);
      return;
    }

    if (farmMode === "new") {
      setSelectedFarm(null);
      setFieldMode("new");
      setSelectedField(null);
      return;
    }

    if (farmMode === "current" && !canUseCurrentFarm) {
      setFarmMode("existing");
    }
  }, [canUseCurrentFarm, currentFarm, farmMode]);

  useEffect(() => {
    if (fieldMode === "current" && canUseCurrentField) {
      setSelectedField(currentField);
      return;
    }

    if (fieldMode === "current" && !canUseCurrentField) {
      setFieldMode(farmMode === "new" ? "new" : "existing");
    }
  }, [canUseCurrentField, currentField, farmMode, fieldMode]);

  const selectedUserInOrganisation = Boolean(
    selectedUser &&
      selectedOrganisation &&
      (selectedUser.organisationMemberships.some(
        (organisation) => organisation.id === selectedOrganisation.id
      ) ||
        (selectedUser.id === currentUser?.id &&
          selectedOrganisation.id === currentOrganisation?.id))
  );
  const shouldWarnAboutUserChange =
    userMode === "change" &&
    selectedUser &&
    !valuesEqual(selectedUser.id, currentUser?.id);
  const shouldWarnAboutMembership =
    Boolean(selectedUser && selectedOrganisation) &&
    !selectedUserInOrganisation;

  const organisationOptions = useMemo(
    () => toSelectionOptions(organisations),
    [organisations]
  );
  const farmOptions = useMemo(() => toSelectionOptions(farms), [farms]);
  const fieldOptions = useMemo(() => toSelectionOptions(fields), [fields]);

  const currentStep = STEPS[stepIndex].key;
  const trimmedUserSearch = userSearch.trim();
  const selectedUserLabel = getUserLabel(selectedUser);
  const selectedFarmLabel =
    farmMode === "new" ? newFarmName.trim() || "New farm" : selectedFarm?.name;
  const selectedFieldLabel =
    fieldMode === "new"
      ? newFieldName.trim() || "New field"
      : selectedField?.name;

  const summaryRows: SummaryRow[] = [
    {
      key: "user",
      label: "User",
      current: getUserLabel(currentUser),
      next: selectedUserLabel,
    },
    {
      key: "organisation",
      label: "Organisation",
      current: currentOrganisation?.name ?? "Unknown organisation",
      next: selectedOrganisation?.name,
    },
    {
      key: "farm",
      label: "Farm",
      current: currentFarm?.name ?? "Unknown farm",
      next: selectedFarmLabel,
    },
    {
      key: "field",
      label: "Field",
      current: currentField?.name ?? "Unknown field",
      next: selectedFieldLabel,
    },
  ];
  const completedSummaryRows = summaryRows.filter(
    (row) => STEPS.findIndex((step) => step.key === row.key) < stepIndex
  );
  const hasValidFarmSelection =
    farmMode === "current"
      ? canUseCurrentFarm
      : farmMode === "new"
        ? newFarmName.trim().length > 0
        : Boolean(selectedFarm?.id);
  const hasValidFieldSelection =
    fieldMode === "current"
      ? canUseCurrentField
      : fieldMode === "new"
        ? newFieldName.trim().length > 0
        : Boolean(selectedField?.id);

  const stepIsValid =
    currentStep === "user"
      ? Boolean(selectedUser)
      : currentStep === "organisation"
        ? Boolean(selectedOrganisation)
        : currentStep === "farm"
          ? hasValidFarmSelection
          : hasValidFieldSelection;

  const canSubmit =
    Boolean(selectedUser?.id && selectedOrganisation?.id) &&
    hasValidFarmSelection &&
    hasValidFieldSelection;

  const displayedError = localError || transferResult.error?.message;
  const shouldShowConfirmationWarning = Boolean(
    shouldWarnAboutUserChange || shouldWarnAboutMembership
  );

  const handleSubmit = async () => {
    if (!canSubmit || !selectedUser || !selectedOrganisation) {
      return;
    }

    setLocalError(null);
    try {
      await transferPesTest({
        variables: {
          input: {
            testId: test.id,
            targetUserId: selectedUser.id,
            targetOrganisationId: selectedOrganisation.id,
            existingFarmId: farmMode === "new" ? null : selectedFarm?.id,
            newFarmName: farmMode === "new" ? newFarmName.trim() : null,
            existingFieldId: fieldMode === "new" ? null : selectedField?.id,
            newFieldName: fieldMode === "new" ? newFieldName.trim() : null,
          },
        },
        refetchQueries: [
          { query: PesTestDocument, variables: { id: test.id } },
        ],
      });
      setConfirmationOpen(false);
      await onTransferred();
    } catch (error) {
      setLocalError(parseServerError(error).message);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !transferResult.loading) {
      setConfirmationOpen(false);
      onCancel();
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Transfer test</DialogTitle>
            <DialogDescription>
              Move this test to a different user, organisation, farm, or field.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <ol className="grid grid-cols-4 gap-2" aria-label="Transfer steps">
              {STEPS.map((step, index) => (
                <li key={step.key}>
                  <div
                    className={cn(
                      "flex h-8 w-full items-center justify-center gap-1.5 rounded-md border px-2 text-xs font-medium",
                      index === stepIndex
                        ? "border-primary/50 bg-primary/5 text-primary"
                        : index < stepIndex
                          ? "border-border bg-muted/50 text-foreground"
                          : "border-border/70 text-muted-foreground"
                    )}
                    aria-current={index === stepIndex ? "step" : undefined}
                  >
                    {index < stepIndex && <Check className="size-3" />}
                    {step.label}
                  </div>
                </li>
              ))}
            </ol>

            <TransferSummary rows={completedSummaryRows} />

            {currentStep === "user" && (
              <div className="space-y-4">
                <ChoiceButtonGroup
                  value={userMode}
                  options={[
                    {
                      value: "current",
                      label: "Current User",
                      description: getUserLabel(currentUser),
                    },
                    {
                      value: "change",
                      label: "Change User",
                      description: "Search for another user",
                    },
                  ]}
                  onChange={(nextMode) => {
                    setUserMode(nextMode);
                    setSelectedUser(
                      nextMode === "current" ? currentUser : null
                    );
                    setLocalError(null);
                  }}
                />

                {userMode === "change" && (
                  <div className="space-y-2">
                    <Label htmlFor="transfer-user-search">User</Label>
                    <Input
                      id="transfer-user-search"
                      value={userSearch}
                      onChange={(event) => setUserSearch(event.target.value)}
                      placeholder="Search by name or email"
                    />
                    {(usersLoading ||
                      trimmedUserSearch.length > 0 ||
                      userOptions.length > 0) && (
                      <div className="max-h-48 overflow-y-auto rounded-md border">
                        {usersLoading ? (
                          <p className="px-3 py-2 text-sm text-muted-foreground">
                            Loading users...
                          </p>
                        ) : userOptions.length === 0 ? (
                          <p className="px-3 py-2 text-sm text-muted-foreground">
                            No users found
                          </p>
                        ) : (
                          userOptions.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              className={cn(
                                "flex w-full flex-col items-start gap-1 px-3 py-2 text-left text-sm hover:bg-accent",
                                selectedUser?.id === user.id && "bg-accent"
                              )}
                              onClick={() => setSelectedUser(user)}
                            >
                              <span className="font-medium">
                                {getUserLabel(user)}
                              </span>
                              {user.email && (
                                <span className="text-xs text-muted-foreground">
                                  {user.email}
                                </span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                {shouldWarnAboutUserChange && (
                  <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                    <AlertTriangle className="size-4" />
                    <AlertDescription className="text-amber-900">
                      Changing the user transfers ownership of this test.
                      Continue only after confirming the target user should own
                      it.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {currentStep === "organisation" && (
              <div className="space-y-4">
                <ChoiceButtonGroup
                  value={organisationMode}
                  options={[
                    {
                      value: "current",
                      label: "Current Organisation",
                      description: currentOrganisation?.name ?? undefined,
                    },
                    {
                      value: "change",
                      label: "Change Organisation",
                      description: "Search for another organisation",
                    },
                  ]}
                  onChange={(nextMode) => {
                    setOrganisationMode(nextMode);
                    if (nextMode === "current") {
                      setSelectedOrganisation(currentOrganisation);
                      setSelectedFarm(currentFarm);
                      setSelectedField(currentField);
                      setFarmMode(currentFarm ? "current" : "existing");
                      setFieldMode(currentField ? "current" : "existing");
                    } else {
                      setSelectedOrganisation(null);
                    }
                    setLocalError(null);
                  }}
                />

                {organisationMode === "change" && (
                  <TransferCombobox
                    placeholder={
                      selectedOrganisation?.name ?? "Select organisation"
                    }
                    options={organisationOptions}
                    loading={organisationsLoading}
                    emptyMessage="No organisations found"
                    searchPlaceholder="Search organisations..."
                    onSelect={(value) => {
                      const organisation =
                        organisations.find((item) => item.id === value) ?? null;
                      setSelectedOrganisation(organisation);
                      setLocalError(null);
                    }}
                  />
                )}

                {shouldWarnAboutMembership && (
                  <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                    <AlertTriangle className="size-4" />
                    <AlertDescription className="text-amber-900">
                      The selected user is not part of this organisation and
                      will be forcibly added.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {currentStep === "farm" && (
              <div className="space-y-4">
                <ChoiceButtonGroup
                  value={farmMode}
                  options={[
                    ...(canUseCurrentFarm
                      ? [
                          {
                            value: "current" as const,
                            label: "Current Farm",
                            description: currentFarm?.name,
                          },
                        ]
                      : []),
                    {
                      value: "existing",
                      label: "Existing Farm",
                      description: "Choose from selected organisation",
                    },
                    {
                      value: "new",
                      label: "New Farm",
                      description: "Create in selected organisation",
                    },
                  ]}
                  onChange={(nextMode) => {
                    setFarmMode(nextMode);
                    setLocalError(null);
                  }}
                />

                {farmMode === "existing" && (
                  <TransferCombobox
                    placeholder={selectedFarm?.name ?? "Select farm"}
                    options={farmOptions}
                    loading={farmsLoading}
                    disabled={!selectedOrganisation}
                    emptyMessage="No farms found"
                    searchPlaceholder="Search farms..."
                    onSelect={(value) => {
                      const farm =
                        farms.find((item) => item.id === value) ?? null;
                      setSelectedFarm(farm);
                      setSelectedField(null);
                      setLocalError(null);
                    }}
                  />
                )}
                {farmMode === "new" && (
                  <div className="space-y-2">
                    <Label htmlFor="transfer-new-farm-name">Farm name</Label>
                    <Input
                      id="transfer-new-farm-name"
                      value={newFarmName}
                      onChange={(event) => setNewFarmName(event.target.value)}
                    />
                  </div>
                )}
              </div>
            )}

            {currentStep === "field" && (
              <div className="space-y-4">
                <ChoiceButtonGroup
                  value={fieldMode}
                  options={[
                    ...(canUseCurrentField
                      ? [
                          {
                            value: "current" as const,
                            label: "Current Field",
                            description: currentField?.name,
                          },
                        ]
                      : []),
                    {
                      value: "existing",
                      label: "Existing Field",
                      description:
                        farmMode === "new"
                          ? "Unavailable for new farms"
                          : "Choose from selected farm",
                      disabled: farmMode === "new",
                    },
                    {
                      value: "new",
                      label: "New Field",
                      description: "Create in selected farm",
                    },
                  ]}
                  onChange={(nextMode) => {
                    setFieldMode(nextMode);
                    setLocalError(null);
                  }}
                />

                {fieldMode === "existing" && (
                  <TransferCombobox
                    placeholder={selectedField?.name ?? "Select field"}
                    options={fieldOptions}
                    loading={fieldsLoading}
                    disabled={!selectedFarm || farmMode === "new"}
                    emptyMessage="No fields found"
                    searchPlaceholder="Search fields..."
                    onSelect={(value) => {
                      const field =
                        fields.find((item) => item.id === value) ?? null;
                      setSelectedField(field);
                      setLocalError(null);
                    }}
                  />
                )}
                {fieldMode === "new" && (
                  <div className="space-y-2">
                    <Label htmlFor="transfer-new-field-name">Field name</Label>
                    <Input
                      id="transfer-new-field-name"
                      value={newFieldName}
                      onChange={(event) => setNewFieldName(event.target.value)}
                    />
                  </div>
                )}
              </div>
            )}

            {displayedError && (
              <p className="text-sm text-destructive">{displayedError}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={transferResult.loading}
            >
              Cancel
            </Button>
            {stepIndex > 0 && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
                disabled={transferResult.loading}
              >
                Back
              </Button>
            )}
            {stepIndex < STEPS.length - 1 ? (
              <Button
                type="button"
                onClick={() =>
                  setStepIndex((index) => Math.min(STEPS.length - 1, index + 1))
                }
                disabled={!stepIsValid}
              >
                Next
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => setConfirmationOpen(true)}
                disabled={!canSubmit || transferResult.loading}
              >
                Review transfer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={confirmationOpen}
        onOpenChange={(open) => {
          if (!open && !transferResult.loading) {
            setConfirmationOpen(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Confirm transfer</DialogTitle>
            <DialogDescription>
              Review the final ownership and location changes before
              transferring this test.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <TransferSummary rows={summaryRows} />

            {shouldShowConfirmationWarning && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle className="size-4" />
                <AlertDescription className="text-amber-900">
                  This transfer includes high-risk ownership or permission
                  changes. Continue only if you know what you are doing.
                </AlertDescription>
              </Alert>
            )}

            {displayedError && (
              <p className="text-sm text-destructive">{displayedError}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmationOpen(false)}
              disabled={transferResult.loading}
            >
              Back to edit
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || transferResult.loading}
            >
              {transferResult.loading ? "Transferring..." : "Confirm transfer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
